import React, { useState } from "react";
import {
  Chip,
  Grid,
  LinearProgress,
  Stack,
  styled,
  Typography,
} from "@mui/material";
import { FacetInfoModal } from "./FacetInfoModal";
import { RichText } from "./RichText";
import type { FacetDefinition } from "./FacetInfoModal";
import { USER_HIGH_INTEREST_THRESHOLD_PERCENT } from "../constants/interestThresholds";

// Bullet PNG embedded as data URL (24x24 RGBA, 345 bytes)
const bulletDataUrl =
  "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABgAAAAYCAYAAADgdz34AAAACXBIWXMAAAsTAAALEwEAmpwYAAAA" +
  "AXNSR0IArs4c6QAAAARnQU1BAACxjwv8YQUAAADuSURBVHgB7ZPNDQFBGIaf2bi5KIEO6IAOSJA4" +
  "iQ6UsCqgg+VMIjpQgg6owcENGZ852Z+x+yV7Yp/DTvLN97dv5oWKn8dkBe2WtRxT8qsv8g3NkI0/" +
  "Jdl8Rx/LnuJcqdExAzcsRZCKWCnQ0eDuXyg1wIw4ynFEg6Etsi4LDXA8mEmR9k/mMqRbaICZiJ6W" +
  "BVoMkY1EsrwBLnfESo4TGixN6vHXF/B9owZagri0NV+e6Bm6jTRYkfbG4TOUbbS9NH5wRosVP4zj" +
  "svpeUYSeMNn8TRlOdtJI81bWVRlOvvKk57v0OXlF0eaGufNNxf/yAqEmP2P8bIuiAAAAAElFTkSu" +
  "QmCC";

export interface FacetTypeConfig {
  facet: string;
  scoringType?: string;
  maxResults?: number;
  resultsHeader?: string;
  color?: string;
  textColor?: string;
  longLabel?: string;
  description?: string;
  id?: string;
}

export interface FacetScore {
  facet: string;
  score: number[];
}

export interface FacetScoreBoxProps {
  facetsToShow: FacetScore[];
  facetType: FacetTypeConfig;
  cmsFacets: FacetDefinition[];
  mode?: "profile" | "completion";
  minHeight?: string;
  renderFallbackIcon?: (
    facet: FacetDefinition,
    size: number,
  ) => React.ReactNode;
}

const scoresToPercentage = (score: number[]): number => {
  if (!Array.isArray(score)) return 0;
  if (score[1] === 0) return 0;
  return Math.round((score[0] / score[1]) * 100);
};

const getLayoutType = (
  displayManyFacets: boolean,
  _topNFacets: boolean,
  top1Facets: boolean,
  mode: string,
): string => {
  if (mode === "profile") {
    if (top1Facets) return "top1-profile";
    return "standard";
  }
  if (!displayManyFacets) return "top1-completion";
  return "standard";
};

export const FacetScoreBox = ({
  facetsToShow,
  facetType,
  cmsFacets,
  mode = "profile",
  minHeight,
  renderFallbackIcon,
}: FacetScoreBoxProps) => {
  const facetTypeName = facetType?.facet;
  const getPrelabel = (index: number, score: number): string | null => {
    if (
      facetTypeName === "Core" &&
      facetType?.scoringType === "all" &&
      score >= USER_HIGH_INTEREST_THRESHOLD_PERCENT
    ) {
      return "HIGH INTEREST";
    } else if (
      index <= 1 &&
      facetType?.scoringType === "all" &&
      score >= USER_HIGH_INTEREST_THRESHOLD_PERCENT
    ) {
      return "Top " + facetTypeName;
    }
    return null;
  };

  const fontColor = facetType?.textColor || "#000000";
  const hideHeader = mode === "completion";

  const displayManyFacets =
    facetType?.scoringType === "all" ||
    (facetType?.scoringType === "topN" && facetsToShow.length > 1);
  const topNFacets = facetType?.scoringType === "topN";
  const top1Facets = facetType?.scoringType === "top1";
  const layoutType = getLayoutType(
    displayManyFacets,
    topNFacets,
    top1Facets,
    mode,
  );

  switch (layoutType) {
    case "top1-completion":
      return (
        <Top1CompletionFacetScoreBox
          facetType={facetType}
          cmsFacets={cmsFacets}
          minHeight={minHeight}
          getPrelabel={getPrelabel}
          facetsToShow={facetsToShow}
        />
      );
    case "top1-profile":
      return (
        <Top1ProfileFacetScoreBox
          facetType={facetType}
          cmsFacets={cmsFacets}
          minHeight={minHeight}
          fontColor={fontColor}
          hideHeader={hideHeader}
          getPrelabel={getPrelabel}
          facetsToShow={facetsToShow}
          mode={mode}
        />
      );
    case "standard":
    default:
      return (
        <StandardFacetScoreBox
          facetType={facetType}
          cmsFacets={cmsFacets}
          minHeight={minHeight}
          fontColor={fontColor}
          hideHeader={hideHeader}
          getPrelabel={getPrelabel}
          facetsToShow={facetsToShow}
          mode={mode}
          renderFallbackIcon={renderFallbackIcon}
        />
      );
  }
};

// --- Sub-components ---

interface InternalProps {
  facetType: FacetTypeConfig;
  cmsFacets: FacetDefinition[];
  minHeight?: string;
  fontColor?: string;
  hideHeader?: boolean;
  getPrelabel: (index: number, score: number) => string | null;
  facetsToShow: FacetScore[];
  mode?: string;
  renderFallbackIcon?: (
    facet: FacetDefinition,
    size: number,
  ) => React.ReactNode;
}

const Top1CompletionFacetScoreBox = ({
  facetType,
  cmsFacets,
  minHeight,
  getPrelabel,
  facetsToShow,
}: InternalProps) => (
  <FacetBox backgroundColor={facetType.color} minHeight={minHeight}>
    {facetsToShow.map(({ facet, score }, i) => (
      <SingleCompletionFacet
        facetPreLabel={getPrelabel(i, scoresToPercentage(score))}
        key={i}
        facet={facet}
        score={score}
        facetType={facetType}
        facetDefinition={cmsFacets.find((f) => f.facetCode === facet)}
      />
    ))}
  </FacetBox>
);

const Top1ProfileFacetScoreBox = ({
  facetType,
  cmsFacets,
  minHeight,
  hideHeader,
  fontColor,
  getPrelabel,
  facetsToShow,
  mode,
}: InternalProps) => (
  <FacetBox backgroundColor={facetType.color} minHeight={minHeight}>
    {!hideHeader && (
      <Grid xs={12} item>
        <Typography sx={{ color: fontColor }} variant="h5">
          {facetType.longLabel}
        </Typography>
        <DefinitionStyle
          sx={{ mt: 1, mb: 2, color: fontColor }}
          variant="body1"
        >
          {facetType.description}
        </DefinitionStyle>
      </Grid>
    )}
    {facetsToShow.map(({ facet, score }, i) => (
      <StandardFacet
        facetPreLabel={getPrelabel(i, scoresToPercentage(score))}
        key={i}
        facet={facet}
        score={score}
        facetType={facetType}
        facetDefinition={cmsFacets.find((f) => f.facetCode === facet)}
        showDescription={true}
        mode={mode}
      />
    ))}
  </FacetBox>
);

const StandardFacetScoreBox = ({
  facetType,
  cmsFacets,
  minHeight,
  hideHeader,
  fontColor,
  getPrelabel,
  facetsToShow,
  mode,
  renderFallbackIcon,
}: InternalProps) => {
  const [selectedFacet, setSelectedFacet] = useState<FacetDefinition | null>(
    null,
  );

  return (
    <>
      <FacetBox
        backgroundColor={facetType.color}
        minHeight={minHeight}
        mode={mode}
      >
        {!hideHeader && mode !== "profile" && (
          <Grid xs={12} item>
            <Typography sx={{ color: fontColor }} variant="h5">
              {facetType.longLabel}
            </Typography>
            <DefinitionStyle
              sx={{ mt: 1, mb: 2, color: fontColor }}
              variant="body1"
            >
              {facetType.description}
            </DefinitionStyle>
          </Grid>
        )}
        {facetsToShow.map(({ facet, score }, i) => (
          <StandardFacet
            facetPreLabel={getPrelabel(i, scoresToPercentage(score))}
            key={i}
            facet={facet}
            score={score}
            facetType={facetType}
            facetDefinition={cmsFacets.find((f) => f.facetCode === facet)}
            showDescription={false}
            onClick={setSelectedFacet}
            mode={mode}
            renderFallbackIcon={renderFallbackIcon}
          />
        ))}
      </FacetBox>
      <FacetInfoModal
        open={!!selectedFacet}
        onClose={() => setSelectedFacet(null)}
        facet={selectedFacet}
        renderFallbackIcon={renderFallbackIcon}
      />
    </>
  );
};

// --- Individual facet components ---

interface StandardFacetProps {
  facet: string;
  score: number[];
  facetDefinition?: FacetDefinition;
  facetType: FacetTypeConfig;
  facetPreLabel: string | null;
  onClick?: (facetDefinition: FacetDefinition) => void;
  showDescription: boolean;
  mode?: string;
  renderFallbackIcon?: (
    facet: FacetDefinition,
    size: number,
  ) => React.ReactNode;
}

const StandardFacet = ({
  facet,
  score,
  facetDefinition,
  facetType,
  facetPreLabel,
  onClick,
  showDescription,
  mode,
  renderFallbackIcon,
}: StandardFacetProps) => {
  if (!Array.isArray(score)) return null;

  const fontColor = facetType.textColor || "#000000";
  const showMeters = facetType?.scoringType === "all";
  const iconSize = mode === "profile" ? 36 : showDescription ? 153 : 48;
  const hasIcon = !!facetDefinition?.icon;

  return (
    <FacetContainer mode={mode}>
      {facetPreLabel && (
        <Chip
          sx={{
            position: "absolute",
            right: "-1px",
            top: "-3px",
            backgroundColor: "#000",
            color: "#fff",
            borderRadius: "6px",
            padding: "0px -6px",
            textTransform: "uppercase",
            height: "24px",
          }}
          label={facetPreLabel}
        />
      )}
      <FacetPill
        backgroundColor={facetDefinition?.secondaryColor}
        onClick={() => onClick?.(facetDefinition!)}
        style={{ cursor: onClick ? "pointer" : undefined }}
        mode={mode}
      >
        {hasIcon && (
          <img
            src={facetDefinition!.icon}
            alt={facetDefinition!.variable}
            width={iconSize}
            height={iconSize}
          />
        )}
        {!hasIcon &&
          renderFallbackIcon &&
          facetDefinition &&
          renderFallbackIcon(facetDefinition, iconSize)}
        <FacetContent mode={mode}>
          <FacetLabel>
            <Typography
              gutterBottom
              sx={{
                fontFamily: "Proxima Nova",
                color: fontColor,
                mb: mode === "profile" ? 0.5 : "auto",
              }}
              variant={mode === "profile" ? "subtitle2" : "subtitle1"}
            >
              {facetDefinition?.longLabel || facet}
            </Typography>
          </FacetLabel>

          {showMeters && (
            <LinearProgress
              variant="determinate"
              value={scoresToPercentage(score)}
              sx={{
                height: mode === "profile" ? "8px" : "16px",
                borderRadius: "16px",
                color: facetDefinition?.color,
                backgroundColor: "#fff",
                "& .MuiLinearProgress-bar": {
                  backgroundColor: facetDefinition?.color,
                },
              }}
            />
          )}
          {showDescription && facetDefinition && (
            <DefinitionStyle sx={{ color: fontColor }} variant="body1">
              <RichText
                value={facetDefinition.description as string | undefined}
              />
            </DefinitionStyle>
          )}
        </FacetContent>
      </FacetPill>
    </FacetContainer>
  );
};

interface SingleCompletionFacetProps {
  facet: string;
  score: number[];
  facetDefinition?: FacetDefinition;
  facetType: FacetTypeConfig;
  facetPreLabel: string | null;
}

const SingleCompletionFacet = ({
  facet,
  score,
  facetDefinition,
  facetType,
}: SingleCompletionFacetProps) => {
  if (!Array.isArray(score)) return null;

  const fontColor = facetType.textColor || "#000000";
  const iconSize = 72;

  return (
    <Stack
      direction="column"
      alignItems="center"
      justifyContent="center"
      gap={1}
      sx={{ m: "15px" }}
    >
      {facetDefinition?.icon && (
        <img
          src={facetDefinition.icon}
          alt={facetDefinition.variable}
          width={iconSize}
          height={iconSize}
        />
      )}

      <Typography
        gutterBottom
        sx={{ fontFamily: "Proxima Nova", color: fontColor }}
        variant="h4"
      >
        {facetDefinition?.longLabel || facet}
      </Typography>

      {facetDefinition && (
        <DefinitionStyle sx={{ color: fontColor, m: "15px" }} variant="body1">
          <RichText value={facetDefinition.description as string | undefined} />
        </DefinitionStyle>
      )}
    </Stack>
  );
};

// --- Styled components ---

const DefinitionStyle = styled(Typography)({
  label: "FacetDefinitionStyle",
  "& ul": {
    paddingLeft: 0,
    paddingRight: "20px",
    listStyle: "none",
    "& li": {
      backgroundImage: `url("${bulletDataUrl}")`,
      backgroundRepeat: "no-repeat",
      backgroundPosition: "left 4px",
      backgroundSize: "12px",
      paddingLeft: "20px",
    },
  },
});

const FacetContainer = styled(Grid)<{ mode?: string }>(({ mode }) => ({
  label: "FacetContainer",
  padding: mode === "profile" ? "4px" : "8px",
  position: "relative",
}));

const FacetPill = styled("div")<{ backgroundColor?: string; mode?: string }>(
  ({ backgroundColor, mode }) => ({
    label: "FacetPill",
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "flex-start",
    gap: 6,
    backgroundColor: backgroundColor || "#e4f8eb",
    padding: mode === "profile" ? "4px 8px" : "10px 15px",
    borderRadius: "16px",
  }),
);

const FacetLabel = styled("div")({
  label: "FacetLabel",
  display: "flex",
  flexDirection: "column",
  alignItems: "stretch",
  justifyContent: "center",
});

const FacetContent = styled("div")<{ mode?: string }>(({ mode }) => ({
  label: "FacetContent",
  display: "flex",
  flexDirection: "column",
  alignItems: "stretch",
  flexGrow: 1,
  marginLeft: mode === "profile" ? "8px" : "15px",
}));

const FacetBox = styled("div")<{
  backgroundColor?: string;
  minHeight?: string;
  mode?: string;
}>(({ backgroundColor, minHeight, mode }) => ({
  label: "FacetBox",
  backgroundColor: backgroundColor || "#e4f8eb",
  borderRadius: "24px",
  backgroundSize: "cover",
  backgroundPosition: "center",
  padding: mode === "profile" ? "16px" : "20px",
  minHeight: minHeight || "auto",
  width: mode === "profile" ? "320px" : "100%",
  maxHeight: mode === "profile" ? "350px" : "auto",
}));
